/**
 * WebView Context Detection Hook
 * Detects if the app is running inside a WebView and retrieves external props
 */

import { useEffect, useState } from 'react';
import { WebViewExternalProps, WebViewMessageType } from '../types';
import { validateExternalProps, sanitizeExternalProps } from '../utils/validators';
import { WebViewMessageService } from '../services/messageService';
import { WEBVIEW_CONSTANTS } from '../utils/constants';

declare global {
  interface Window {
    whiteboardExternalProps?: WebViewExternalProps;
  }
}

export function useWebViewContext() {
  const [isWebView, setIsWebView] = useState(false);
  const [externalProps, setExternalProps] = useState<WebViewExternalProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const messageService = WebViewMessageService.getInstance();
    let mounted = true;

    const detectWebViewContext = async () => {
      try {
        // Check if we're on the /webview route
        const isWebViewRoute = window.location.pathname === '/webview' || window.location.pathname.startsWith('/webview/');
        
        // If not on webview route, skip detection entirely
        if (!isWebViewRoute) {
          console.log('Not on /webview route, skipping WebView detection');
          return;
        }
        
        // Check if we're in an iframe or WebView
        const isInIframe = window.parent !== window;
        const hasOpener = window.opener !== null;
        const hasReactNativeWebView = !!(window as any).ReactNativeWebView;
        
        // Only log detection info if we have WebView indicators
        if (isInIframe || hasOpener || hasReactNativeWebView || window.location.search.includes('isWebView=true')) {
          console.log('WebView Detection:', {
            isInIframe,
            hasOpener,
            hasReactNativeWebView,
            hasWhiteboardExternalProps: !!window.whiteboardExternalProps,
            hasEdesisTahtaConfig: !!(window as any).edesisTahtaConfig,
            windowLocation: window.location.href
          });
        }
        
        // Check for window.whiteboardExternalProps
        if (window.whiteboardExternalProps) {
          console.log('WebView: Found window.whiteboardExternalProps', window.whiteboardExternalProps);
          
          // Log the structure for debugging
          const props = window.whiteboardExternalProps;
          console.log('WebView: Props structure:', {
            hasAuthConfig: !!props.authConfig,
            authConfigKeys: props.authConfig ? Object.keys(props.authConfig) : [],
            hasStudyTask: !!props.studyTask,
            hasUiConfig: !!props.uiConfig,
            hasFeatures: !!props.features
          });
          
          if (validateExternalProps(window.whiteboardExternalProps)) {
            const sanitized = sanitizeExternalProps(window.whiteboardExternalProps);
            console.log('WebView: Props validated and sanitized successfully');
            
            if (mounted) {
              setExternalProps(sanitized);
              setIsWebView(true);
              
              // Store in localStorage for persistence
              localStorage.setItem(
                WEBVIEW_CONSTANTS.STORAGE_KEYS.EXTERNAL_PROPS,
                JSON.stringify(sanitized)
              );
            }
          } else {
            console.error('WebView: External props validation failed');
            // Log specific validation issues
            if (!props.authConfig) {
              console.error('Missing authConfig');
            } else {
              const authConfig = props.authConfig;
              if (!authConfig.userInfo) console.error('Missing authConfig.userInfo');
              if (!authConfig.accessToken) console.error('Missing authConfig.accessToken');
              if (!authConfig.firebaseAuthUrl) console.error('Missing authConfig.firebaseAuthUrl');
              if (!authConfig.firebaseAuthRootUrl) console.error('Missing authConfig.firebaseAuthRootUrl');
              if (!('tenantId' in authConfig)) console.error('Missing authConfig.tenantId');
            }
            throw new Error('Invalid external props structure');
          }
        }

        // Set up message handler for dynamic props updates
        messageService.on(WebViewMessageType.SET_EXTERNAL_PROPS, (data) => {
          console.log('WebView: Received setExternalProps message');
          
          if (validateExternalProps(data)) {
            const sanitized = sanitizeExternalProps(data);
            
            if (mounted) {
              setExternalProps(sanitized);
              setIsWebView(true);
              
              // Store in localStorage
              localStorage.setItem(
                WEBVIEW_CONSTANTS.STORAGE_KEYS.EXTERNAL_PROPS,
                JSON.stringify(sanitized)
              );
            }
          } else {
            console.error('WebView: Invalid props received via message');
          }
        });

        // Check edesisTahtaConfig if whiteboardExternalProps not found
        if (!window.whiteboardExternalProps && (window as any).edesisTahtaConfig) {
          console.log('WebView: Found window.edesisTahtaConfig, converting to whiteboardExternalProps');
          
          const config = (window as any).edesisTahtaConfig;
          
          // Extract questions from coursesGroups if not directly available
          let allQuestions = config.testConfig?.examData?.questions || [];
          if (!allQuestions.length && config.testConfig?.examData?.coursesGroups) {
            // Flatten questions from all courses
            allQuestions = config.testConfig.examData.coursesGroups.flatMap((group: any) => 
              group.courses?.flatMap((course: any) => course.questions || []) || []
            );
          }
          
          const convertedProps = {
            authConfig: config.authConfig,
            testConfig: config.testConfig,
            questions: allQuestions,
            coursesGroups: config.testConfig?.examData?.coursesGroups || [],
            dersGrupListesi: config.testConfig?.examData?.coursesGroups || [],
            uiConfig: config.uiConfig,
            dataService: config.dataService,
            callbacks: config.callbacks,
            options: config.options
          };
          
          console.log('WebView: Converted props:', {
            hasAuth: !!convertedProps.authConfig,
            tenantId: convertedProps.authConfig?.tenantId,
            questionsCount: convertedProps.questions?.length,
            coursesGroupsCount: convertedProps.coursesGroups?.length
          });
          
          window.whiteboardExternalProps = convertedProps;
          
          if (validateExternalProps(convertedProps)) {
            const sanitized = sanitizeExternalProps(convertedProps);
            
            if (mounted) {
              setExternalProps(sanitized);
              setIsWebView(true);
              
              localStorage.setItem(
                WEBVIEW_CONSTANTS.STORAGE_KEYS.EXTERNAL_PROPS,
                JSON.stringify(sanitized)
              );
            }
          }
        }
        
        // Check localStorage for previously stored props
        if (!externalProps && !window.whiteboardExternalProps) {
          const storedProps = localStorage.getItem(WEBVIEW_CONSTANTS.STORAGE_KEYS.EXTERNAL_PROPS);
          if (storedProps) {
            try {
              const parsed = JSON.parse(storedProps);
              if (validateExternalProps(parsed)) {
                if (mounted) {
                  setExternalProps(parsed);
                  setIsWebView(true);
                }
              }
            } catch (e) {
              console.error('WebView: Failed to parse stored props');
            }
          }
        }

        // Check URL params for WebView indicators
        const urlParams = new URLSearchParams(window.location.search);
        const isWebViewParam = urlParams.get('isWebView') === 'true';
        const platformParam = urlParams.get('platform');
        const externalModeParam = urlParams.get('externalMode') === 'true';
        
        if (isWebViewParam || externalModeParam) {
          console.log('WebView: Detected via URL params', {
            isWebView: isWebViewParam,
            platform: platformParam,
            externalMode: externalModeParam
          });
          setIsWebView(true);
        }
        
        // Send ready message if we're in WebView context
        if (isInIframe || hasOpener || hasReactNativeWebView || isWebViewParam) {
          messageService.sendReady();
        }

      } catch (err) {
        console.error('WebView context detection error:', err);
        if (mounted) {
          setError(err as Error);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    // Run detection immediately
    detectWebViewContext();

    // Poll for injected data ONLY if we're on /webview route AND have WebView indicators
    const isWebViewRoute = window.location.pathname === '/webview' || window.location.pathname.startsWith('/webview/');
    const urlParams = new URLSearchParams(window.location.search);
    const isWebViewParam = urlParams.get('isWebView') === 'true';
    const isInIframe = window.parent !== window;
    const hasReactNativeWebView = !!(window as any).ReactNativeWebView;
    
    let pollCount = 0;
    let pollInterval: NodeJS.Timeout | null = null;
    
    // Only poll if we're on webview route AND have clear indicators of being in WebView
    if (isWebViewRoute && (isWebViewParam || isInIframe || hasReactNativeWebView)) {
      console.log('WebView: Starting polling for injected data...');
      
      pollInterval = setInterval(() => {
        pollCount++;
        
        // Check for various possible injection methods
        const hasWhiteboardExternalProps = !!window.whiteboardExternalProps;
        const hasEdesisTahtaConfig = !!(window as any).edesisTahtaConfig;
        
        // Only log every 5th attempt to reduce spam
        if (pollCount % 5 === 0) {
          console.log(`WebView: Polling attempt ${pollCount}`, {
            hasWhiteboardExternalProps,
            hasEdesisTahtaConfig
          });
        }
        
        // If we found data or exceeded max attempts, stop polling
        if (hasWhiteboardExternalProps || hasEdesisTahtaConfig || pollCount > 20) {
          console.log('WebView: Stopping poll, found data or exceeded attempts');
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          detectWebViewContext();
        }
      }, 100); // Poll every 100ms
    }

    // Also check after a delay (for late prop injection)
    const timeoutId = setTimeout(() => {
      detectWebViewContext();
    }, WEBVIEW_CONSTANTS.TIMEOUTS.INIT_CHECK);
    
    // Listen for custom event that might be dispatched when data is injected
    const handleWebViewDataInjected = () => {
      console.log('WebView: webview-data-injected event received');
      detectWebViewContext();
    };
    
    window.addEventListener('webview-data-injected', handleWebViewDataInjected);
    
    // Listen for postMessage from React Native
    const handleMessage = (event: MessageEvent) => {
      // Filter out React DevTools messages to avoid spam
      if (event.data && typeof event.data === 'object') {
        const source = event.data.source;
        if (source && (source.includes('react-devtools') || source === 'react-devtools-bridge' || source === 'react-devtools-content-script')) {
          // Skip React DevTools messages
          return;
        }
      }
      
      console.log('WebView: Received postMessage:', event.data);
      
      // Check if it's configuration data from React Native
      if (event.data && typeof event.data === 'object') {
        // Handle setExternalProps message from React Native WebView
        if (event.data.type === 'setExternalProps' && event.data.data) {
          console.log('WebView: Received setExternalProps message from React Native');
          
          // Set the data on window object
          window.whiteboardExternalProps = event.data.data;
          
          // Run detection again
          detectWebViewContext();
        }
        // Handle other config formats
        else if (event.data.edesisTahtaConfig || event.data.whiteboardExternalProps) {
          console.log('WebView: Received config via postMessage');
          
          // Set the data on window object
          if (event.data.edesisTahtaConfig) {
            (window as any).edesisTahtaConfig = event.data.edesisTahtaConfig;
          }
          if (event.data.whiteboardExternalProps) {
            window.whiteboardExternalProps = event.data.whiteboardExternalProps;
          }
          
          // Run detection again
          detectWebViewContext();
        }
      }
    };
    
    window.addEventListener('message', handleMessage);

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('webview-data-injected', handleWebViewDataInjected);
      messageService.off(WebViewMessageType.SET_EXTERNAL_PROPS);
    };
  }, []);

  return {
    isWebView,
    externalProps,
    isLoading,
    error,
  };
}